import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Delete, Eye, EyeOff } from 'lucide-react';
import { authApi } from '@/api/authApi';
import type { LoginResponse, WorkspaceStaffEntry, WorkspaceStaffListResponse } from '@/api/authApi';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ForceChangePasswordPage } from './ForceChangePasswordPage';

const ROLE_BADGE: Record<string, 'default' | 'success' | 'neutral' | 'warning'> = {
  Owner: 'default',
  Manager: 'success',
  ServiceAdvisor: 'warning',
  PartClerk: 'neutral',
  Receptionist: 'neutral',
};

function getInitials(name: string) {
  const parts = name.trim().split(' ');
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function FloatInput({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        required
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder=" "
        className="peer block w-full rounded-wl border-2 border-neutral-300 bg-white px-3 py-3.5 text-sm text-on-surface transition-colors focus:border-primary-600 focus:outline-none"
      />
      <label className={`pointer-events-none absolute left-3 px-1 text-neutral-500 transition-all peer-focus:-top-2.5 peer-focus:bg-white peer-focus:text-xs peer-focus:text-primary-600 ${value.length > 0 ? '-top-2.5 bg-white text-xs' : 'top-3.5 bg-transparent text-sm'}`}>
        {label}
      </label>
    </div>
  );
}

function PasswordFloatInput({ label, value, onChange }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        required
        autoComplete="current-password"
        placeholder=" "
        className="peer block w-full rounded-wl border-2 border-neutral-300 bg-white px-3 py-3.5 pr-10 text-sm text-on-surface transition-colors focus:border-primary-600 focus:outline-none"
      />
      <label className={`pointer-events-none absolute left-3 px-1 text-neutral-500 transition-all peer-focus:-top-2.5 peer-focus:bg-white peer-focus:text-xs peer-focus:text-primary-600 ${value.length > 0 ? '-top-2.5 bg-white text-xs' : 'top-3.5 bg-transparent text-sm'}`}>
        {label}
      </label>
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow(v => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600 transition-colors"
        aria-label={show ? 'Hide password' : 'Show password'}
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function WorkspaceLoginPage() {
  const navigate = useNavigate();
  const { setAuth, setStaffSession } = useAuthStore();
  const { setTenant } = useTenantStore();

  const [step, setStep] = useState<'slug' | 'staff'>('slug');
  const [slugInput, setSlugInput] = useState('');
  const [slugLoading, setSlugLoading] = useState(false);
  const [slugError, setSlugError] = useState<string | null>(null);
  const [workspace, setWorkspace] = useState<WorkspaceStaffListResponse | null>(null);

  const [selected, setSelected] = useState<WorkspaceStaffEntry | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [failCount, setFailCount] = useState(0);

  const [forceChangePassword, setForceChangePassword] = useState(false);

  async function handleSlugSubmit(e: FormEvent) {
    e.preventDefault();
    const slug = slugInput.trim().toLowerCase();
    if (!slug) return;
    setSlugLoading(true);
    setSlugError(null);
    try {
      const result = await authApi.workspaceListStaff(slug);
      setWorkspace(result);
      setStep('staff');
    } catch (err: unknown) {
      const ax = err as { response?: { status: number } };
      if (ax?.response?.status === 404) {
        setSlugError('No active shop found for this tenant slug. Check the slug and try again.');
      } else {
        setSlugError('Could not connect. Check your internet connection and try again.');
      }
    } finally {
      setSlugLoading(false);
    }
  }

  function openCard(member: WorkspaceStaffEntry) {
    setSelected(member);
    setUsername('');
    setPassword('');
    setPin('');
    setAuthError(null);
    setFailCount(0);
  }

  function closeModal() {
    setSelected(null);
    setUsername('');
    setPassword('');
    setPin('');
    setAuthError(null);
  }

  function handlePinKey(digit: string) {
    if (pin.length < 6) setPin(p => p + digit);
  }

  function handlePinBackspace() {
    setPin(p => p.slice(0, -1));
  }

  function applyLoginSuccess(data: LoginResponse) {
    const token = data.accessToken ?? data.token ?? '';
    setAuth(token, data.slug, data.sessionId ?? '');
    setTenant({
      tenantId: data.tenantId,
      slug: data.slug,
      companyName: data.companyName,
      displayName: data.displayName,
      storageMode: data.storageMode as 'CloudS3' | 'Local',
    });
    try { localStorage.setItem('wl.workspace', data.slug); } catch { /* ignore */ }
    setStaffSession(data.staffToken!, data.staffId!, data.staffRole!, data.staffFullName!);
    if (data.staffMustChangePassword) {
      setForceChangePassword(true);
      closeModal();
    } else {
      navigate('/dashboard');
    }
  }

  function handleLoginError(err: unknown, isPin: boolean) {
    const ax = err as { response?: { status: number; data?: { error?: string; message?: string; retryAfterMinutes?: number } } };
    const nextFails = failCount + 1;
    setFailCount(nextFails);
    if (isPin) setPin(''); else setPassword('');
    if (ax?.response?.data?.error === 'TECHNICIAN_PORTAL_ONLY') {
      setAuthError('Technicians use the Technician Intake Portal, not the shop management portal.');
    } else if (ax?.response?.data?.error === 'STAFF_INACTIVE') {
      setAuthError('This account is inactive. Contact your shop owner.');
    } else if (ax?.response?.status === 401) {
      setAuthError(nextFails >= 3
        ? 'Incorrect credentials. Contact your shop owner to reset your access.'
        : isPin ? 'Incorrect PIN. Try again.' : 'Incorrect username or password. Try again.');
    } else if (ax?.response?.status === 403 && ax?.response?.data?.error === 'SESSION_LIMIT_REACHED') {
      setAuthError(ax.response.data.message ?? `Session limit reached. Idle sessions clear automatically within ${ax.response.data.retryAfterMinutes ?? 5} minutes. Please try again shortly.`);
    } else {
      setAuthError('An error occurred. Please try again.');
    }
  }

  async function handleSignIn(e: FormEvent) {
    e.preventDefault();
    if (!selected || !workspace) return;
    setAuthLoading(true);
    setAuthError(null);
    try {
      const data = await authApi.workspaceStaffLogin({
        slug: workspace.slug,
        username: username.trim(),
        password,
      });
      applyLoginSuccess(data);
    } catch (err) {
      handleLoginError(err, false);
    } finally {
      setAuthLoading(false);
    }
  }

  async function handlePinConfirm() {
    if (pin.length < 4 || !selected || !workspace) return;
    setAuthLoading(true);
    setAuthError(null);
    try {
      const data = await authApi.workspaceStaffLogin({
        slug: workspace.slug,
        staffId: selected.staffId,
        pin,
      });
      applyLoginSuccess(data);
    } catch (err) {
      handleLoginError(err, true);
    } finally {
      setAuthLoading(false);
    }
  }

  if (forceChangePassword) {
    return (
      <ForceChangePasswordPage
        tempPassword=""
        onSuccess={() => navigate('/dashboard')}
      />
    );
  }

  return (
    <div className="flex h-screen overflow-hidden">
      <div className="hidden lg:block w-3/5 flex-shrink-0">
        <img src="/login-bg.png" alt="" aria-hidden="true" className="h-full w-full object-cover" />
      </div>

      <div className="w-full lg:w-2/5 flex flex-col items-center justify-center bg-white overflow-y-auto px-8 py-12">
        <div className="w-full max-w-[380px]">
          <div className="flex justify-center mb-8">
            <img src="/logo.png" alt="WrenchLab" className="h-20 w-auto object-contain" />
          </div>

          {step === 'slug' ? (
            <>
              <h1 className="text-[28px] font-bold text-on-surface mb-1">Shop sign in</h1>
              <p className="text-sm text-neutral-500 mb-6">
                Enter your shop's tenant slug to continue.
              </p>

              {slugError && (
                <div className="mb-4 rounded-wl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {slugError}
                </div>
              )}

              <form onSubmit={handleSlugSubmit} className="space-y-5">
                <FloatInput
                  label="Tenant Slug"
                  value={slugInput}
                  onChange={v => setSlugInput(v.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  autoFocus
                />
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full min-h-[48px] text-sm justify-center"
                  loading={slugLoading}
                >
                  Continue
                </Button>
              </form>

              <div className="mt-8 text-center">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-neutral-500 hover:underline"
                >
                  ← Back to Owner Sign In
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-[28px] font-bold text-on-surface mb-1">
                {workspace?.companyName ?? 'Welcome'}
              </h1>
              <p className="text-sm text-neutral-500 mb-6">Select your name to sign in.</p>

              {workspace && workspace.staff.length === 0 ? (
                <p className="text-sm text-neutral-400 text-center py-8">
                  No staff accounts are set up yet. Contact your shop owner.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {workspace?.staff.map(member => (
                    <button
                      key={member.staffId}
                      type="button"
                      onClick={() => openCard(member)}
                      className="wl-card p-4 flex flex-col items-center gap-2 hover:shadow-md hover:border-primary-300 transition-all group"
                    >
                      <div className="w-12 h-12 rounded-full bg-primary-100 group-hover:bg-primary-200 flex items-center justify-center transition-colors">
                        <span className="text-base font-bold text-primary-700">{getInitials(member.fullName)}</span>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-neutral-900 leading-tight">{member.fullName}</p>
                        <div className="mt-1">
                          <Badge variant={ROLE_BADGE[member.role] ?? 'neutral'}>
                            {member.role}
                          </Badge>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setStep('slug'); setWorkspace(null); setSlugError(null); }}
                  className="text-sm text-neutral-500 hover:underline"
                >
                  ← Change workspace
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <Modal
        open={!!selected}
        title={`Sign in as ${selected?.fullName ?? ''}`}
        onClose={closeModal}
        size="sm"
      >
        {selected?.hasPassword ? (
          <form onSubmit={handleSignIn} className="space-y-4">
            {authError && (
              <div className="rounded-wl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {authError}
              </div>
            )}
            <FloatInput
              label="Username or email"
              value={username}
              onChange={setUsername}
              autoFocus
            />
            <PasswordFloatInput
              label="Password"
              value={password}
              onChange={setPassword}
            />
            <Button
              type="submit"
              variant="primary"
              className="w-full min-h-[48px] text-sm justify-center"
              loading={authLoading}
            >
              Sign In
            </Button>
          </form>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div className="flex gap-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${
                    i < pin.length
                      ? 'bg-primary-600 border-primary-600'
                      : 'bg-neutral-100 border-neutral-300'
                  }`}
                >
                  {i < pin.length && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                </div>
              ))}
            </div>
            {authError && (
              <p className="text-sm text-red-600 text-center">{authError}</p>
            )}
            <div className="grid grid-cols-3 gap-2 w-48">
              {['1','2','3','4','5','6','7','8','9'].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => handlePinKey(d)}
                  className="h-12 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-900 font-semibold text-lg transition-colors"
                >
                  {d}
                </button>
              ))}
              <div />
              <button
                type="button"
                onClick={() => handlePinKey('0')}
                className="h-12 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-900 font-semibold text-lg transition-colors"
              >
                0
              </button>
              <button
                type="button"
                onClick={handlePinBackspace}
                className="h-12 rounded-xl bg-neutral-100 hover:bg-neutral-200 text-neutral-500 transition-colors flex items-center justify-center"
              >
                <Delete className="h-5 w-5" />
              </button>
            </div>
            <Button
              variant="primary"
              className="w-full"
              onClick={handlePinConfirm}
              loading={authLoading}
              disabled={pin.length < 4}
            >
              Confirm
            </Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
